import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { AlertTriangle, Trash2, Database, Package, FileText, ShoppingCart, Loader2, Shield } from 'lucide-react';
import api from '../../services/api';

const CATEGORY_META = {
  master: { label: 'Master Data', icon: Database, color: 'blue', desc: 'Core catalog & reference data' },
  transactional: { label: 'Transactional Data', icon: ShoppingCart, color: 'amber', desc: 'Orders, subscriptions, deliveries' },
  stock: { label: 'Stock Data', icon: Package, color: 'teal', desc: 'Current inventory levels' },
  financial: { label: 'Financial Data', icon: FileText, color: 'red', desc: 'Chart of accounts & ledger entries' },
};

const DataManagement = () => {
  const { toast } = useToast();
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/data/collections');
      setCollections(res.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load data stats', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const toggleSelect = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectCategory = (category) => {
    const keys = collections.filter(c => c.category === category).map(c => c.key);
    const allSelected = keys.every(k => selected.includes(k));
    if (allSelected) {
      setSelected(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelected(prev => [...new Set([...prev, ...keys])]);
    }
  };

  const selectAll = () => {
    if (selected.length === collections.length) {
      setSelected([]);
    } else {
      setSelected(collections.map(c => c.key));
    }
  };

  const selectedCount = collections.filter(c => selected.includes(c.key)).reduce((sum, c) => sum + c.count, 0);

  const handlePurge = async () => {
    if (confirmText !== 'DELETE ALL') return;
    try {
      setPurging(true);
      const res = await api.post('/admin/data/purge', { collections: selected, confirm: 'DELETE ALL' });
      toast({ title: 'Data Purged', description: res.data.message });
      setShowConfirm(false);
      setConfirmText('');
      setSelected([]);
      fetchStats();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Purge failed', variant: 'destructive' });
    } finally {
      setPurging(false);
    }
  };

  const grouped = {};
  collections.forEach(c => {
    if (!grouped[c.category]) grouped[c.category] = [];
    grouped[c.category].push(c);
  });

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="data-management-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Data Management</h1>
            <p className="text-sm text-slate-500">Mass delete master data, transactional records, stock, and financial data</p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="warning-banner">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">Danger Zone — Irreversible Action</p>
            <p className="text-xs text-red-600 mt-0.5">Deleting data cannot be undone. User accounts are protected and cannot be deleted from this page.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Select All */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={selectAll}
                className="text-sm font-medium text-slate-600 hover:text-red-600 transition-colors"
                data-testid="select-all-btn"
              >
                {selected.length === collections.length ? 'Deselect All' : 'Select All'}
              </button>
              {selected.length > 0 && (
                <span className="text-xs font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full" data-testid="selected-count">
                  {selected.length} collections selected &middot; {selectedCount.toLocaleString()} total records
                </span>
              )}
            </div>

            {/* Grouped Collections */}
            {Object.entries(CATEGORY_META).map(([cat, meta]) => {
              const items = grouped[cat] || [];
              if (!items.length) return null;
              const Icon = meta.icon;
              const catKeys = items.map(i => i.key);
              const allCatSelected = catKeys.every(k => selected.includes(k));
              const catCount = items.reduce((s, i) => s + i.count, 0);

              return (
                <div key={cat} className="mb-5" data-testid={`category-${cat}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 text-${meta.color}-600`} />
                      <h2 className="text-sm font-bold text-slate-700">{meta.label}</h2>
                      <span className="text-xs text-slate-400">{meta.desc}</span>
                    </div>
                    <button
                      onClick={() => selectCategory(cat)}
                      className={`text-xs font-medium px-2 py-0.5 rounded transition-colors ${
                        allCatSelected ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-600'
                      }`}
                      data-testid={`select-category-${cat}`}
                    >
                      {allCatSelected ? 'Deselect' : 'Select'} All ({catCount})
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {items.map(item => (
                      <label
                        key={item.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selected.includes(item.key)
                            ? 'border-red-300 bg-red-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`collection-${item.key}`}
                      >
                        <Checkbox
                          checked={selected.includes(item.key)}
                          onCheckedChange={() => toggleSelect(item.key)}
                          data-testid={`checkbox-${item.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{item.label}</p>
                          <p className="text-xs text-slate-400">{item.key}</p>
                        </div>
                        <span className={`text-sm font-bold tabular-nums ${item.count > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
                          {item.count.toLocaleString()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Delete Button */}
            <div className="mt-6 pt-6 border-t">
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={selected.length === 0}
                variant="destructive"
                className="w-full sm:w-auto h-11"
                data-testid="open-purge-dialog-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected Data ({selected.length} collections)
              </Button>
            </div>
          </>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="max-w-md" data-testid="purge-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" /> Confirm Mass Delete
              </DialogTitle>
              <DialogDescription>
                This will permanently delete <strong>{selectedCount.toLocaleString()} records</strong> from{' '}
                <strong>{selected.length} collections</strong>. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-3">
              <p className="text-xs font-semibold text-slate-500 mb-2">Collections to purge:</p>
              <div className="flex flex-wrap gap-1 mb-4">
                {selected.map(key => {
                  const item = collections.find(c => c.key === key);
                  return (
                    <span key={key} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      {item?.label || key} ({item?.count || 0})
                    </span>
                  );
                })}
              </div>
              <label className="text-sm font-medium text-slate-700">
                Type <span className="font-bold text-red-600">DELETE ALL</span> to confirm:
              </label>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="Type DELETE ALL"
                className="mt-1"
                data-testid="confirm-text-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowConfirm(false); setConfirmText(''); }} data-testid="cancel-purge-btn">
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={confirmText !== 'DELETE ALL' || purging}
                onClick={handlePurge}
                data-testid="confirm-purge-btn"
              >
                {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {purging ? 'Deleting...' : 'Delete All Selected'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DataManagement;
